import React, { useEffect, useRef } from "react";
import axios from "axios";

export const useOutsideClick = (ref: React.RefObject<HTMLElement | null>, cb: Function) => {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (ref?.current && !ref.current.contains(event.target as Node)) {
        cb(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, cb]);
};

export const catchError = (error: unknown, defaultErrorMessage: string) => {
  let message;

  if (axios.isAxiosError<{ error?: { message: string } }>(error)) {
    message = error.response?.data.error?.message;
  } else if (error instanceof Error) message = error.message;

  return {
    success: false,
    message: message || defaultErrorMessage,
  };
};

export const useTyping = (inputProp: any, callback: Function) => {
  const typingInterval = useRef<{
    timestamp: number;
    timer: null | number;
  }>({
    timestamp: 0,
    timer: null,
  });

  useEffect(() => {
    // typingInterval.current = typingInterval;

    const currentStamp = new Date().getTime();
    const diffStamp = currentStamp - typingInterval.current.timestamp;

    typingInterval.current.timestamp = currentStamp;

    // If last keypressed was 2 sec ago send event.
    if (diffStamp > 2000) {
      callback();
    }

    // If not timeout seted
    if (!typingInterval.current.timer) {
      typingInterval.current.timer = window.setTimeout(function () {
        const currentTime = new Date().getTime();
        const _diffStamp = currentTime - typingInterval.current.timestamp;
        if (_diffStamp < 2000) {
          callback();
        }

        typingInterval.current.timer = null;
      }, 2000);
    }
  }, [inputProp]);
};

export const fetchRetry = async (optionsArr: Array<any>, fetcher: Function, n: number, timeout = 5000) => {
  let error;
  let i = 0;

  for (; i < n; i++) {
    if (i > 0) await new Promise((resolve) => setTimeout(resolve, timeout));

    try {
      var response = await fetcher.apply(this, optionsArr);

      if (response.status == 200 || response.success) return { success: true, data: response.data };
    } catch (err) {
      error = { i, err };
    }
  }

  return {
    success: false,
    message: error,
  };
};

export const sleep = (timeout = 1000) => new Promise((resolve) => setTimeout(resolve, timeout));

export const checkExtension = async () => {
  if (!window?.chrome?.runtime) return false;
  const extId = import.meta.env.VITE_APP_EXTENSION_ID;
  return new Promise((resolve) =>
    window.chrome.runtime.sendMessage(extId, { type: "SF/CHECK" }, (response: any) => {
      if (response && response.success) resolve(response.version);
      resolve(false);
    }),
  );
};

export const sendChromeEvent = async (event: { type: string; payload?: any }, callback?: Function) => {
  return new Promise((resolve, reject) => {
    try {
      window.chrome.runtime.sendMessage(import.meta.env.VITE_APP_EXTENSION_ID, event, (response: any) => {
        if (response && callback) {
          callback(response);
        }

        resolve(response);
      });
    } catch (ex) {
      reject((ex as any).message);
    }
  });
};

export class Caret {
  /**
   * get/set caret position
   * @param {HTMLColletion} target
   */

  isContentEditable: string;
  target: HTMLInputElement | HTMLTextAreaElement;

  constructor(target: HTMLInputElement | HTMLTextAreaElement) {
    this.isContentEditable = target && target.contentEditable;
    this.target = target;
  }
  /**
   * get caret position
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Range}
   * @returns {number}
   */
  getPos() {
    // for texterea/input element
    return this.target.selectionStart;
  }

  /**
   * set caret position
   * @param {number} pos - caret position
   */
  setPos(pos: number) {
    // for contentedit field
    this.target.setSelectionRange(pos, pos);
    this.target.blur();
    this.target.focus();
  }
}

export function isDev() {
  return window.location.hostname !== "softphone.icom-libras.com.br";
}

export function playDTMFTone(tone: string, duration = 150) {
  // Frequências DTMF padrão (Hz)
  const dtmfFrequencies: Record<string, [number, number]> = {
    "1": [697, 1209],
    "2": [697, 1336],
    "3": [697, 1477],
    "4": [770, 1209],
    "5": [770, 1336],
    "6": [770, 1477],
    "7": [852, 1209],
    "8": [852, 1336],
    "9": [852, 1477],
    "*": [941, 1209],
    "0": [941, 1336],
    "#": [941, 1477],
  };

  const freqs = dtmfFrequencies[tone];
  if (!freqs) return;

  // Cria contexto de áudio
  const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();

  // Cria dois osciladores (baixa e alta frequência)
  const osc1 = audioCtx.createOscillator();
  const osc2 = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc1.frequency.value = freqs[0];
  osc2.frequency.value = freqs[1];
  gain.gain.value = 0.1; // volume baixo pra não irritar o ouvido

  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(audioCtx.destination);

  // Inicia e para após X ms
  osc1.start();
  osc2.start();
  setTimeout(() => {
    osc1.stop();
    osc2.stop();
    audioCtx.close();
  }, duration);
}

export function filterPhoneNumbersChars(phoneNumber: string) {
  return phoneNumber.replace(/[^0-9+#*]/g, "");
}

export const isStage = window.location.hostname.split(".")[0] === "stage";
